import {AnyStylePrimitive} from '@packages/systems/core/utils/StyleVariables';
import type {WebflowCssProperty} from '@packages/systems/core/utils/WebflowCssSubset';
import type {UnitConversions, Unit} from '@packages/systems/style-legacy/utils';

export type SectionType = (typeof Section)[keyof typeof Section];

const EXPANDED_CSS = 'expandedCss';
const BACKGROUND = 'background';
const BORDER = 'border';
const LAYOUT = 'layout';
const SPACING = 'spacing';
const SIZING = 'sizing';
const POSITION = 'position';
const SHADOWS = 'shadows';
const TRANSITIONS_AND_TRANSFORMS = 'transitionsAndTransforms';
const TYPOGRAPHY = 'typography';
const EFFECTS = 'effects';
const BOX = 'box';
const OTHER = 'other';

export const Section = {
  EXPANDED_CSS,
  BACKGROUND,
  BORDER,
  LAYOUT,
  SPACING,
  SIZING,
  POSITION,
  SHADOWS,
  TRANSITIONS_AND_TRANSFORMS,
  TYPOGRAPHY,
  EFFECTS,
  BOX,
  OTHER,
} as const;

/**
 * Contains information about each CSS property that is supported by Webflow.
 * NOTE: this is only to be used inside of this package to support typing
 * the path.
 *
 * Use the CssPropertyDetail type instead for the full type.
 *
 * ```js
 * import type {CssPropertyDetail} from '@packages/systems/style-legacy/css-properties';
 * ```
 */
export type CssPropertyDetail_Internal<Path, CssPropertyName> = {
  /** The path to the style system so it knows where and how to get and update property values */
  path: Path;
  section?: SectionType;
  default?: number | string;
  allowSwatches?: boolean;
  supportedVariableTypes?: AnyStylePrimitive['type'][];
  dimensional?: boolean;
  transforms?: {
    normal: number;
    bold?: number;
  };
  /**
   * Is the property inheritable? It's named "typographic" for legacy reasons, but
   * in reality it is whether or not the property is inheritable. From the stylesystem.md
   * document:
   *
   * > One noteworthy part of this process is that for UI reasons we don't want
   * > to include in the styleMap any upstream-sourced non-typographic style
   * > properties for rules that are considered typographic-only. This is
   * > because we need to include typographic or "inheriting" rules in the
   * > styleMap chain computation per the CSS spec, but we don't want these
   * > rules to count for inheritance of non-typographic properties, or else
   * > the UI will display incorrect inheritance.
   */
  typographic?: boolean;
  defaultUnit?: Unit;
  combo?: string;
  placeholder?: string | number;
  placeholderUnit?: Unit;
  conversions?: UnitConversions;
  units?: Unit[];
  forceUnit?: Unit;
  multi?: boolean;
  min?: number;
  max?: number;
  from?: Record<any, any>;
  value?: Record<any, any>;
  prop?: WebflowCssProperty | `--${string}`;
  color?: boolean;
  prefix?: string;
  computedFrom?: Array<CssPropertyName>;
  computed?: Array<CssPropertyName>;
};
